import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL, COMPANY } from "@/lib/data";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/leistungen", label: "Leistungen" },
  { to: "/ueber-uns", label: "Über uns" },
  { to: "/faq", label: "FAQ" },
  { to: "/kontakt", label: "Kontakt" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || !isHome || open;
  const closeMobile = () => setOpen(false);

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
      data-testid="site-navbar"
      className={[
        "fixed top-0 inset-x-0 z-50 transition-all duration-300",
        solid
          ? "bg-black/70 backdrop-blur-2xl border-b border-white/[0.06] shadow-[0_2px_24px_rgba(0,0,0,0.4)]"
          : "bg-transparent border-b border-transparent",
      ].join(" ")}
    >
      <div className="container-x flex items-center justify-between h-[72px] lg:h-[84px]">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src={LOGO_URL}
            alt="Messinger KFZ-Sachverständiger"
            className="h-8 lg:h-9 w-auto max-w-[180px] object-contain transition-all duration-300"
            style={{
              filter: "invert(1) brightness(2)",
            }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                [
                  "relative px-4 py-2 text-sm font-medium transition-colors text-white/85",
                  "hover:text-messinger-orange",
                  isActive ? "text-messinger-orange" : "",
                ].join(" ")
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  <span
                    className={[
                      "absolute left-4 right-4 -bottom-0.5 h-[2px] bg-messinger-orange transition-all duration-300 origin-left",
                      isActive ? "scale-x-100" : "scale-x-0",
                    ].join(" ")}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            data-testid="nav-call-button"
            href={COMPANY.phoneHref}
            className="text-sm font-medium px-4 py-2 rounded-full border border-white/15 text-white hover:bg-white/5 hover:border-white/30 transition-all"
          >
            Anrufen
          </a>
          <Link
            to="/kontakt"
            data-testid="nav-cta-button"
            className="text-sm font-medium px-5 py-2.5 rounded-full bg-messinger-orange text-white hover:bg-messinger-orange-dark hover:shadow-[0_8px_24px_-8px_rgba(232,115,44,0.6)] transition-all"
          >
            Termin anfragen
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2 rounded-sm text-white"
          onClick={() => setOpen((s) => !s)}
          aria-label="Menü"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="lg:hidden border-t border-white/10 bg-messinger-bg/95 backdrop-blur-xl"
          data-testid="nav-mobile-menu"
        >
          <div className="container-x py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={closeMobile}
                data-testid={`nav-mobile-link-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) =>
                  [
                    "px-3 py-3 text-base rounded-sm",
                    isActive
                      ? "text-messinger-orange bg-messinger-orange/10"
                      : "text-white/90",
                  ].join(" ")
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="flex gap-2 pt-3">
              <a
                href={COMPANY.phoneHref}
                onClick={closeMobile}
                className="flex-1 text-center text-sm font-medium px-4 py-3 border border-white/20 text-white rounded-full"
              >
                Anrufen
              </a>
              <Link
                to="/kontakt"
                onClick={closeMobile}
                className="flex-1 text-center text-sm font-semibold px-4 py-3 bg-messinger-orange text-white rounded-full"
              >
                Termin
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
